import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	shift,
	size,
	useFloating,
	type VirtualElement,
} from "@floating-ui/react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { SlashMenuList } from "./slash-menu-list";
import type { SlashMenuItem, SlashMenuRef } from "./slash-menu-types";

export type { SlashMenuItem, SlashMenuRef } from "./slash-menu-types";

export type SlashMenuProps = {
	items: SlashMenuItem[];
	command: (item: SlashMenuItem) => void;
	clientRect: (() => DOMRect | null) | null;
};

const ZERO_RECT = {
	x: 0,
	y: 0,
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	width: 0,
	height: 0,
} as DOMRect;

export const SlashMenu = forwardRef<SlashMenuRef, SlashMenuProps>(function SlashMenu(
	{ items, command, clientRect },
	ref,
) {
	const [selectedIndex, setSelectedIndex] = useState(0);

	useEffect(() => {
		setSelectedIndex(0);
	}, [items]);

	const virtualReference = useMemo<VirtualElement>(
		() => ({
			getBoundingClientRect: () => clientRect?.() ?? ZERO_RECT,
		}),
		[clientRect],
	);

	const menu = useFloating({
		placement: "bottom-start",
		strategy: "fixed",
		middleware: [
			offset(6),
			flip({ padding: 8 }),
			shift({ padding: 8 }),
			size({
				padding: 8,
				apply({ availableHeight, elements }) {
					Object.assign(elements.floating.style, {
						maxHeight: `${Math.min(320, availableHeight)}px`,
					});
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	useEffect(() => {
		menu.refs.setPositionReference(virtualReference);
	}, [menu.refs, virtualReference]);

	function commit(index: number) {
		const item = items[index];
		if (item) command(item);
	}

	useImperativeHandle(ref, () => ({
		onKeyDown(event: KeyboardEvent) {
			if (items.length === 0) return false;
			if (event.key === "ArrowUp") {
				event.preventDefault();
				setSelectedIndex((i) => (i + items.length - 1) % items.length);
				return true;
			}
			if (event.key === "ArrowDown") {
				event.preventDefault();
				setSelectedIndex((i) => (i + 1) % items.length);
				return true;
			}
			if (event.key === "Enter") {
				event.preventDefault();
				commit(selectedIndex);
				return true;
			}
			return false;
		},
	}));

	return (
		<FloatingPortal>
			<SlashMenuList
				items={items}
				selectedIndex={selectedIndex}
				onHover={setSelectedIndex}
				onPick={commit}
				floatingRef={menu.refs.setFloating}
				floatingStyle={menu.floatingStyles}
			/>
		</FloatingPortal>
	);
});
